import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { FloatingPanel, RobbieMark } from '../ui';
import { useFeedbackForm } from '../../hooks/useFeedbackForm';
import FeedbackKindChoices from './FeedbackKindChoices';
import FeedbackComposer from './FeedbackComposer';

/**
 * Robbie's feedback panel — a helper that sits in the corner, not a modal that
 * takes the screen hostage. There is no backdrop and nothing behind it is
 * disabled: someone reporting a bug should be able to look at the bug.
 *
 * On a phone it becomes a bottom sheet, because a floating card pinned to a
 * corner of a 375px screen is just a small modal in a bad place.
 *
 * Escape closes. Cmd/Ctrl+Enter sends (in the composer). Every transition is
 * `motion-reduce:`-guarded, so a reduced-motion setting gets the same panel
 * without the movement.
 */

export interface FeedbackPanelProps {
  open: boolean;
  onClose: () => void;
  /** First name, for the thank-you. Omitted just drops the name. */
  personName?: string;
}

interface FeedbackPanelBodyProps {
  onClose: () => void;
  personName?: string;
}

const HEADLINE = "Tell me what's working and what isn't.";

/** The thank-you, in Robbie's voice: the consequence, then what happens next. */
const thankYou = (name?: string): string =>
  `Got it — thank you${name ? `, ${name}` : ''}. If it's a bug I'll get someone on it; if it's an idea it goes straight to the team.`;

/**
 * The panel's contents. Mounted only while open, which is deliberate: opening
 * the panel again is a new thing to say, so it starts at step one rather than
 * resuming a thank-you somebody already read. Unmounting is the reset.
 */
const FeedbackPanelBody: React.FC<FeedbackPanelBodyProps> = ({ onClose, personName }) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const form = useFeedbackForm(onClose);

  return (
    <div
      // Corner helper on a desktop; full-width sheet docked to the bottom edge
      // on a phone. `pointer-events-none` on the positioner keeps the rest of
      // the page clickable — only the panel itself takes the pointer.
      className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-center sm:inset-x-auto sm:bottom-s7 sm:right-s7 sm:justify-end"
    >
      <FloatingPanel
        ai
        className="pointer-events-auto w-full max-w-[380px] rounded-b-none sm:rounded-panel motion-safe:animate-feedback-rise"
      >
        <div ref={panelRef} role="dialog" aria-label="Give feedback" className="flex flex-col gap-s5 px-s6 py-s5">
          <div className="flex items-start gap-s4">
            <RobbieMark size="sm" state={form.robbieState} aria-label={`Robbie — ${form.robbieState}`} />
            <p className="min-w-0 flex-1 text-body-md leading-[1.5] text-ink-body">
              {form.step === 'sent' ? thankYou(personName) : HEADLINE}
            </p>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close feedback"
              className="-mr-s2 -mt-s1 flex h-7 w-7 shrink-0 items-center justify-center rounded-control text-ink-muted hover:bg-surface-hover hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-action"
            >
              <X className="h-[15px] w-[15px]" />
            </button>
          </div>

          {form.step === 'choose' && <FeedbackKindChoices onChoose={form.chooseKind} />}

          {form.step === 'write' && form.kind && (
            <FeedbackComposer
              kind={form.kind}
              message={form.message}
              context={form.context}
              sending={form.sending}
              error={form.error}
              canSend={form.canSend}
              onMessage={form.setMessage}
              onBack={form.back}
              onSend={form.send}
            />
          )}
        </div>
      </FloatingPanel>
    </div>
  );
};

export const FeedbackPanel: React.FC<FeedbackPanelProps> = ({ open, onClose, personName }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return <FeedbackPanelBody onClose={onClose} personName={personName} />;
};

export default FeedbackPanel;
